const LIVESTREAM = "Livestream Signing";
const SHOUTOUT = "Shoutout";
const AUDIO_SHOUTOUT = "Audio Shoutout";

const PRESET_SPLIT = 5000;
const AUTOGRAPH_GROUPS = new Set(["Funko", "Print", "Poster"]);

export const OTHER_PRODUCT_TYPE = Object.freeze({
  productTypeId: null,
  name: "Other",
});

const typeWhere = (productTypes, flag) =>
  productTypes.find((productType) => productType[flag])?.productTypeId;

const PRESETS = Object.freeze({
  autograph: ({ productTypes, shop }) => ({
    productTypeId: typeWhere(
      productTypes,
      shop.isDomestic ? "isDefaultDomestic" : "isDefaultInternational",
    ),
    signedTypes: [LIVESTREAM],
  }),
  videoShoutout: ({ productTypes }) => ({
    productTypeId: typeWhere(productTypes, "isDefaultShoutout"),
    signedTypes: [SHOUTOUT],
  }),
  audioShoutout: ({ productTypes }) => ({
    productTypeId: typeWhere(productTypes, "isDefaultAudioShoutout"),
    signedTypes: [AUDIO_SHOUTOUT],
  }),
  advanced: () => ({ productTypeId: undefined, signedTypes: [] }),
});

export const presetFor = ({ preset, productTypes, shop }) => {
  const { productTypeId, signedTypes } = PRESETS[preset]({
    productTypes,
    shop,
  });

  return {
    preset,
    productTypeId,
    signedTypes,
    splits: Object.fromEntries(signedTypes.map((s) => [s, PRESET_SPLIT])),
  };
};

export const autographTypeOptions = (productTypes) => [
  ...productTypes.filter(
    (productType) =>
      productType.isDefault && AUTOGRAPH_GROUPS.has(productType.groupName),
  ),
  OTHER_PRODUCT_TYPE,
];

const emptyCard = (number) => ({
  cardId: `card-${number}`,
  name: "",
  subtext: "",
  images: [],
});

export const createComposer = ({ shop }) => ({
  shop,
  preset: null,
  productTypeId: undefined,
  signedTypes: [],
  stock: {},
  signers: [],
  tags: [],
  characterTagString: null,
  franchiseTagString: null,
  characters: {},
  services: [],
  cards: [],
  nextCardNumber: 1,
  isDirty: false,
});

const withEntry = (object, key, value) => ({ ...object, [key]: value });

const withoutEntry = (object, key) =>
  Object.fromEntries(Object.entries(object).filter(([k]) => k !== key));

const moved = (items, from, to) => {
  const rest = items.filter((_, index) => index !== from);

  return [...rest.slice(0, to), items[from], ...rest.slice(to)];
};

const isUntouched = (card) =>
  !card.name && !card.subtext && card.images.length === 0;

const mapCard = (state, cardId, change) => ({
  ...state,
  cards: state.cards.map((card) =>
    card.cardId === cardId ? change(card) : card,
  ),
});

const mapSigners = (state, change) => ({
  ...state,
  signers: state.signers.map(change),
});

const choosePreset = (state, { preset }) => ({
  ...createComposer({ shop: state.shop }),
  preset: preset.preset,
  productTypeId: preset.productTypeId,
  signedTypes: preset.signedTypes,
  stock: Object.fromEntries(preset.signedTypes.map((s) => [s, null])),
  signers: [
    {
      influencerId: state.shop.influencerId,
      name: state.shop.name,
      avatarUrl: state.shop.avatarUrl,
      noFulfillment: false,
      splits: preset.splits,
    },
  ],
  cards: [emptyCard(1)],
  nextCardNumber: 2,
});

const startingSplit = (state, signer) =>
  signer.influencerId === state.shop.influencerId ? PRESET_SPLIT : 0;

const turnOn = (state, signedType) => ({
  ...mapSigners(state, (signer) => ({
    ...signer,
    splits: withEntry(signer.splits, signedType, startingSplit(state, signer)),
  })),
  signedTypes: [...state.signedTypes, signedType],
  stock: withEntry(state.stock, signedType, null),
});

const turnOff = (state, signedType) => ({
  ...mapSigners(state, (signer) => ({
    ...signer,
    splits: withoutEntry(signer.splits, signedType),
  })),
  signedTypes: state.signedTypes.filter((s) => s !== signedType),
  stock: withoutEntry(state.stock, signedType),
});

const addSigner = (state, { signer }) =>
  state.signers.some((s) => s.influencerId === signer.influencerId)
    ? state
    : {
        ...state,
        signers: [
          ...state.signers,
          {
            ...signer,
            noFulfillment: false,
            splits: Object.fromEntries(state.signedTypes.map((s) => [s, 0])),
          },
        ],
      };

const HANDLERS = Object.freeze({
  choosePreset,
  setProductType: (state, { productTypeId }) => ({ ...state, productTypeId }),
  updateCard: (state, { cardId, changes }) =>
    mapCard(state, cardId, (card) => ({ ...card, ...changes })),
  addImage: (state, { cardId, image }) =>
    mapCard(state, cardId, (card) => ({
      ...card,
      images: [...card.images, image],
    })),
  updateImage: (state, { imageId, changes }) => ({
    ...state,
    cards: state.cards.map((card) => ({
      ...card,
      images: card.images.map((image) =>
        image.imageId === imageId ? { ...image, ...changes } : image,
      ),
    })),
  }),
  addCard: (state, { name = "" }) => ({
    ...state,
    cards: [...state.cards, { ...emptyCard(state.nextCardNumber), name }],
    nextCardNumber: state.nextCardNumber + 1,
  }),
  addCardWithImage: (state, { name = "", image }) => {
    const untouched = state.cards.find(isUntouched);

    return untouched
      ? mapCard(state, untouched.cardId, (card) => ({
          ...card,
          name,
          images: [image],
        }))
      : {
          ...state,
          cards: [
            ...state.cards,
            { ...emptyCard(state.nextCardNumber), name, images: [image] },
          ],
          nextCardNumber: state.nextCardNumber + 1,
        };
  },
  removeCard: (state, { cardId }) => ({
    ...state,
    cards: state.cards.filter((card) => card.cardId !== cardId),
  }),
  moveImages: (state, { fromCardId, toCardId }) => {
    const moving = state.cards.find(
      (card) => card.cardId === fromCardId,
    ).images;

    return {
      ...state,
      cards: state.cards
        .filter((card) => card.cardId !== fromCardId)
        .map((card) =>
          card.cardId === toCardId
            ? { ...card, images: [...card.images, ...moving] }
            : card,
        ),
    };
  },
  removeImage: (state, { cardId, imageId }) =>
    mapCard(state, cardId, (card) => ({
      ...card,
      images: card.images.filter((image) => image.imageId !== imageId),
    })),
  moveImage: (state, { cardId, from, to }) =>
    mapCard(state, cardId, (card) => ({
      ...card,
      images: moved(card.images, from, to),
    })),
  toggleSignedType: (state, { signedType }) =>
    state.signedTypes.includes(signedType)
      ? turnOff(state, signedType)
      : turnOn(state, signedType),
  setSplit: (state, { influencerId, signedType, amount }) =>
    mapSigners(state, (signer) =>
      signer.influencerId === influencerId
        ? { ...signer, splits: withEntry(signer.splits, signedType, amount) }
        : signer,
    ),
  setStock: (state, { signedType, stock }) => ({
    ...state,
    stock: withEntry(state.stock, signedType, stock),
  }),
  addSigner,
  removeSigner: (state, { influencerId }) => ({
    ...state,
    signers: state.signers.filter((s) => s.influencerId !== influencerId),
  }),
  setTags: (state, { tags }) => ({ ...state, tags }),
  setServices: (state, { services }) => ({ ...state, services }),
  toggleService: (state, { influencerServiceId, enabled }) => ({
    ...state,
    services: [
      ...state.services.filter(
        (service) => service.influencerServiceId !== influencerServiceId,
      ),
      { influencerServiceId, active: enabled },
    ],
  }),
  setSpecialTags: (state, { characterTagString, franchiseTagString }) => ({
    ...state,
    characterTagString,
    franchiseTagString,
  }),
  setCharacters: (state, { characters }) => ({ ...state, characters }),
  setSetting: (state, { key, value }) => ({
    ...state,
    settings: withEntry(state.settings, key, value),
  }),
});

export const composerReducer = (state, action) =>
  HANDLERS[action.type]
    ? { ...HANDLERS[action.type](state, action), isDirty: true }
    : state;

export const rowsOf = ({ cards, signedTypes }) =>
  cards.flatMap(({ cardId }) =>
    signedTypes.map((signedType) => ({ cardId, signedType })),
  );

const requestImage = ({
  imageUrl,
  originalImageUrl,
  croppedOriginalImageUrl,
}) => ({
  imageUrl,
  originalImageUrl,
  croppedOriginalImageUrl,
});

export const toBatchRequest = (state) => ({
  influencerId: state.shop.influencerId,
  productTypeId: state.productTypeId ?? null,
  signedTypes: state.signedTypes,
  stock: state.stock,
  signers: state.signers.map(({ influencerId, noFulfillment, splits }) => ({
    influencerId,
    noFulfillment,
    splits,
  })),
  tags: state.tags,
  characterTagString: state.characterTagString,
  franchiseTagString: state.franchiseTagString,
  characters: state.characters,
  services: state.services,
  cards: state.cards.map(({ cardId, name, subtext, images }) => ({
    cardId,
    name,
    subtext,
    images: images.map(requestImage),
  })),
});

const UNGROUPED = "Other types";

export const groupedTypeMatches = ({ productTypes, query }) => {
  const needle = query.trim().toLowerCase();
  const matches = productTypes.filter((productType) =>
    productType.name.toLowerCase().includes(needle),
  );
  const groupNames = [
    ...new Set(
      matches.map((productType) => productType.groupName ?? UNGROUPED),
    ),
  ];

  return groupNames.map((groupName) => ({
    groupName,
    productTypes: matches.filter(
      (productType) => (productType.groupName ?? UNGROUPED) === groupName,
    ),
  }));
};
